import { useEffect, useState } from "react";
import api, { fileUrl } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Loader2, FileText, ExternalLink, X, Check, Building2 } from "lucide-react";

/**
 * DoctorVerification — Super Admin panel for reviewing NMC submissions.
 * GET /api/superadmin/doctors/verification?status=pending|verified|rejected|all
 * POST /api/superadmin/doctors/{id}/verify
 * POST /api/superadmin/doctors/{id}/reject  { reason }
 */
export default function DoctorVerification() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectDoctor, setRejectDoctor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(null);  // doctor.id being acted on

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/superadmin/doctors/verification?status=${status}`);
      setRows(data.doctors || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const verify = async (d) => {
    setBusy(d.id);
    try {
      await api.post(`/superadmin/doctors/${d.id}/verify`);
      toast.success(`${d.name} verified`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  const openReject = (d) => {
    setRejectDoctor(d);
    setRejectReason("");
  };

  const submitReject = async () => {
    if (!rejectDoctor) return;
    setBusy(rejectDoctor.id);
    try {
      await api.post(`/superadmin/doctors/${rejectDoctor.id}/reject`, {
        reason: rejectReason || "Documents did not meet verification standards.",
      });
      toast.message(`${rejectDoctor.name} rejected`);
      setRejectDoctor(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  return (
    <Card className="bg-slate-800 border-slate-700 p-4" data-testid="sa-doctor-verification-panel">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-300" />
          <h2 className="font-semibold text-white">Doctor NMC / MCI Verification</h2>
        </div>
        <Badge variant="outline" className="border-slate-600 text-slate-300">
          {rows.length} {status}
        </Badge>
      </div>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="bg-slate-900 border border-slate-700">
          <TabsTrigger value="pending" data-testid="verif-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="verified" data-testid="verif-tab-verified">Verified</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="verif-tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="verif-tab-all">All</TabsTrigger>
        </TabsList>

        <TabsContent value={status} className="mt-3">
          {loading ? (
            <div className="text-slate-400 text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-slate-500 text-sm py-6 text-center" data-testid="verif-empty">
              No doctors in the “{status}” bucket.
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((d) => (
                <div
                  key={d.id}
                  className="rounded-lg border border-slate-700 bg-slate-900 p-3 flex items-center gap-3"
                  data-testid={`verif-row-${d.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center overflow-hidden shrink-0">
                    {d.profile_pic ? (
                      <img src={fileUrl(d.profile_pic)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ShieldCheck className="w-4 h-4 text-slate-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white truncate">{d.name}</div>
                    <div className="text-xs text-slate-400 truncate">
                      {d.specialization_en}
                      {d.org?.name && <> · <Building2 className="w-3 h-3 inline mb-0.5" /> {d.org.name}{d.org.city ? `, ${d.org.city}` : ""}</>}
                    </div>
                    <div className="text-xs mt-1 flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-slate-300">NMC: {d.nmc_number}</span>
                      {d.nmc_certificate_url && (
                        <a
                          href={fileUrl(d.nmc_certificate_url)}
                          target="_blank" rel="noopener noreferrer"
                          className="text-sky-400 hover:underline inline-flex items-center gap-1"
                          data-testid={`verif-cert-${d.id}`}
                        >
                          <FileText className="w-3 h-3" /> View certificate <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {status === "rejected" && d.nmc_reject_reason && (
                        <span className="text-rose-300">· {d.nmc_reject_reason}</span>
                      )}
                    </div>
                  </div>
                  <StatusPill doctor={d} />
                  {status !== "verified" && (
                    <Button
                      size="sm"
                      onClick={() => verify(d)}
                      disabled={busy === d.id}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1 h-8"
                      data-testid={`verif-approve-${d.id}`}
                    >
                      {busy === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                      Verify
                    </Button>
                  )}
                  {status !== "rejected" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openReject(d)}
                      disabled={busy === d.id}
                      className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 gap-1 h-8"
                      data-testid={`verif-reject-${d.id}`}
                    >
                      <X className="w-3 h-3" /> Reject
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!rejectDoctor} onOpenChange={(v) => !v && setRejectDoctor(null)}>
        <DialogContent data-testid="verif-reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <ShieldOff className="w-5 h-5" /> Reject {rejectDoctor?.name}?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            The doctor will see this reason and can re-submit corrected documents.
          </p>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Certificate blurry / registration number does not match NMC portal"
            rows={3}
            maxLength={280}
            data-testid="verif-reject-reason-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectDoctor(null)} disabled={busy === rejectDoctor?.id}>Cancel</Button>
            <Button
              onClick={submitReject}
              disabled={busy === rejectDoctor?.id}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="verif-reject-submit"
            >
              {busy === rejectDoctor?.id ? "Rejecting…" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function StatusPill({ doctor }) {
  if (doctor.verified) return (
    <Badge className="bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/20 gap-1">
      <ShieldCheck className="w-3 h-3" /> Verified
    </Badge>
  );
  if (doctor.verification_status === "rejected") return (
    <Badge className="bg-rose-500/20 text-rose-300 hover:bg-rose-500/20 gap-1">
      <ShieldOff className="w-3 h-3" /> Rejected
    </Badge>
  );
  return (
    <Badge className="bg-amber-500/20 text-amber-300 hover:bg-amber-500/20 gap-1">
      Pending
    </Badge>
  );
}
